import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import * as api from "../lib/api";
import "./CreateKabile.css";

export default function CreateKabile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: "",
    maxMembers: 10,
    minTP: 0,
    description: "",
  });
  
  const [flagImage, setFlagImage] = useState<File | null>(null);
  const [flagPreview, setFlagPreview] = useState<string>("");
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImage, setTempImage] = useState<string>("");
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === "maxMembers" || name === "minTP" ? parseInt(value) || 0 : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFlagUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, flag: "Lütfen bir resim dosyası seçin" }));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, flag: "Dosya boyutu en fazla 2MB olabilir" }));
      return;
    }

    setErrors(prev => ({ ...prev, flag: "" }));

    // Load image for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setTempImage(reader.result as string);
      setShowCropModal(true);
      
      // Initialize crop area (center 80% of image)
      const img = new Image();
      img.onload = () => {
        const width = Math.min(img.width * 0.8, 400);
        const height = Math.min(img.height * 0.8, 300);
        const x = (img.width - width) / 2;
        const y = (img.height - height) / 2;
        setCropArea({ x, y, width, height });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
    
    setFlagImage(file);
  };

  const handleCropComplete = () => {
    // Create cropped image
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return;
      
      // Container boyutları
      const containerWidth = 800;
      const containerHeight = 600;
      
      // Gerçek resim boyutları
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;
      
      // object-fit: contain ile resmin container'daki görünen boyutlarını hesapla
      const imgAspect = imgWidth / imgHeight;
      const containerAspect = containerWidth / containerHeight;
      
      let displayWidth, displayHeight, offsetX, offsetY;
      
      if (imgAspect > containerAspect) {
        // Resim yatay olarak daha geniş - genişliğe göre ölçekle
        displayWidth = containerWidth;
        displayHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - displayHeight) / 2;
      } else {
        // Resim dikey olarak daha uzun - yüksekliğe göre ölçekle
        displayHeight = containerHeight;
        displayWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - displayWidth) / 2;
        offsetY = 0;
      }
      
      // Crop koordinatlarını gerçek resim koordinatlarına çevir
      const scaleX = imgWidth / displayWidth;
      const scaleY = imgHeight / displayHeight;
      
      const sourceX = Math.max(0, (cropArea.x - offsetX) * scaleX);
      const sourceY = Math.max(0, (cropArea.y - offsetY) * scaleY);
      const sourceWidth = Math.min(cropArea.width * scaleX, imgWidth - sourceX);
      const sourceHeight = Math.min(cropArea.height * scaleY, imgHeight - sourceY);
      
      console.log('📏 Resim boyutları:', { imgWidth, imgHeight });
      console.log('📏 Display boyutları:', { displayWidth, displayHeight, offsetX, offsetY });
      console.log('📏 Crop area:', cropArea);
      console.log('📏 Source:', { sourceX, sourceY, sourceWidth, sourceHeight });
      
      // Canvas'ı source boyutunda yap (aspect ratio korusun)
      canvas.width = sourceWidth;
      canvas.height = sourceHeight;
      
      ctx.drawImage(
        img,
        sourceX, sourceY, sourceWidth, sourceHeight,
        0, 0, sourceWidth, sourceHeight // Canvas'ı tam doldur
      );
      
      // Convert canvas to blob and create File object
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File([blob], 'kabile-flag.png', { type: 'image/png' });
          setFlagImage(croppedFile);
          console.log('✅ Cropped flag image saved:', croppedFile);
          console.log('📐 Crop coordinates:', { sourceX, sourceY, sourceWidth, sourceHeight });
          
          // Preview'ı ve modal durumunu blob oluştuktan sonra güncelle
          setFlagPreview(canvas.toDataURL('image/png'));
          setShowCropModal(false);
          setTempImage("");
        }
      }, 'image/png');
    };
    img.src = tempImage;
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempImage("");
    setFlagImage(null);
    const fileInput = document.getElementById('flag') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - cropArea.x, y: e.clientY - cropArea.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const img = document.getElementById('crop-image') as HTMLImageElement;
    if (!img) return;
    
    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    
    let newX = (e.clientX - rect.left) * scaleX - dragStart.x;
    let newY = (e.clientY - rect.top) * scaleY - dragStart.y;
    
    // Constrain to image boundaries
    newX = Math.max(0, Math.min(newX, img.naturalWidth - cropArea.width));
    newY = Math.max(0, Math.min(newY, img.naturalHeight - cropArea.height));
    
    setCropArea(prev => ({ ...prev, x: newX, y: newY }));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleResize = (direction: string, delta: number) => {
    const img = document.getElementById('crop-image') as HTMLImageElement;
    if (!img) return;
    
    setCropArea(prev => {
      let newArea = { ...prev };
      
      if (direction === 'width') {
        newArea.width = Math.max(100, Math.min(prev.width + delta, img.naturalWidth - prev.x));
      } else if (direction === 'height') {
        newArea.height = Math.max(100, Math.min(prev.height + delta, img.naturalHeight - prev.y));
      }
      
      return newArea;
    });
  };

  const removeFlagImage = () => {
    setFlagImage(null);
    setFlagPreview("");
    const fileInput = document.getElementById('flag') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Kabile ismi zorunludur";
    } else if (formData.name.length < 3) {
      newErrors.name = "Kabile ismi en az 3 karakter olmalıdır";
    } else if (formData.name.length > 30) {
      newErrors.name = "Kabile ismi en fazla 30 karakter olabilir";
    }
    
    if (formData.maxMembers < 5) {
      newErrors.maxMembers = "En az 5 kişi olmalıdır";
    } else if (formData.maxMembers > 100) {
      newErrors.maxMembers = "En fazla 100 kişi olabilir";
    }
    
    if (formData.minTP < 0) {
      newErrors.minTP = "TP puanı negatif olamaz";
    } else if (formData.minTP > 1000000) {
      newErrors.minTP = "TP puanı çok yüksek";
    }
    
    if (formData.description && formData.description.length > 200) {
      newErrors.description = "Açıklama en fazla 200 karakter olabilir";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create kabile
      const response = await api.createKabile(formData);
      const kabileId = response.kabileId;
      console.log('✅ Kabile oluşturuldu:', kabileId);
      
      // If flag image exists, upload it
      console.log('🔍 Flag image kontrolü:', flagImage);
      if (flagImage) {
        console.log('📤 Bayrak yükleniyor...');
        const flagFormData = new FormData();
        flagFormData.append('flag', flagImage);
        const uploadResult = await api.uploadKabileFlag(kabileId, flagFormData);
        console.log('✅ Bayrak yüklendi:', uploadResult.flagUrl);
      } else {
        console.log('⚠️ Bayrak bulunamadı, yükleme yapılmadı');
      }
      
      // Navigate to my kabile page after creation
      navigate("/savkabile/my");
      
    } catch (error: any) {
      // If user already has a kabile, redirect to my kabile page
      if (error.message?.includes("Zaten bir kabileye")) {
        navigate("/savkabile/my");
        return;
      }
      setErrors({ submit: error.message || "Kabile oluşturulurken bir hata oluştu" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="create-kabile-container">
        <div className="create-kabile-content">
          <div className="auth-required">
            <h2>🔒 Giriş Gerekli</h2>
            <p>Kabile oluşturmak için giriş yapmanız gerekmektedir.</p>
            <button onClick={() => navigate("/login")} className="login-button">
              Giriş Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-kabile-container">
      <div className="create-kabile-content">
        <header className="create-kabile-header">
          <button onClick={() => navigate("/savkabile")} className="back-button">
            ← Geri
          </button>
          <h1 className="create-kabile-title">
            🏛️ Kabile Oluştur
          </h1>
        </header>

        <form onSubmit={handleSubmit} className="kabile-form">
          {/* Kabile İsmi */}
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Kabile İsmi *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-input ${errors.name ? 'input-error' : ''}`}
              placeholder="Örn: Altın Savaşçılar"
              maxLength={30}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
            <span className="input-hint">{formData.name.length}/30 karakter</span>
          </div>

          {/* Kabile Bayrağı */}
          <div className="form-group">
            <label htmlFor="flag" className="form-label">
              Kabile Bayrağı (Opsiyonel)
            </label>
            
            {flagPreview ? (
              <div className="flag-preview-container">
                <div className="flag-preview">
                  <img src={flagPreview} alt="Kabile Bayrağı" className="flag-image waving-flag" />
                  <button 
                    type="button" 
                    onClick={removeFlagImage}
                    className="remove-flag-button"
                    title="Bayrağı Kaldır"
                  >
                    ✕
                  </button>
                </div>
                <span className="input-hint">
                  🚩 Bu bayrak kabile sıralamasında dalgalanacak
                </span>
              </div>
            ) : (
              <div className="flag-upload-area">
                <input
                  type="file"
                  id="flag"
                  name="flag"
                  accept="image/*"
                  onChange={handleFlagUpload}
                  className="flag-input"
                />
                <label htmlFor="flag" className="flag-upload-label">
                  <div className="upload-icon">🚩</div>
                  <div className="upload-text">
                    <strong>Bayrak Yükle</strong>
                    <span>PNG, JPG, GIF (Max 2MB)</span>
                  </div>
                </label>
              </div>
            )}
            
            {errors.flag && <span className="error-message">{errors.flag}</span>}
            <span className="input-hint">
              Kabilenizi temsil edecek özel bir bayrak tasarlayın
            </span>
          </div>

          {/* Maksimum Üye Sayısı */}
          <div className="form-group">
            <label htmlFor="maxMembers" className="form-label">
              Maksimum Üye Sayısı *
            </label>
            <input
              type="number"
              id="maxMembers"
              name="maxMembers"
              value={formData.maxMembers}
              onChange={handleChange}
              className={`form-input ${errors.maxMembers ? 'input-error' : ''}`}
              min="5"
              max="100"
            />
            {errors.maxMembers && <span className="error-message">{errors.maxMembers}</span>}
            <span className="input-hint">5-100 kişi arasında</span>
          </div>

          {/* Minimum TP Puanı */}
          <div className="form-group">
            <label htmlFor="minTP" className="form-label">
              Minimum SavWar TP Puanı *
            </label>
            <input
              type="number"
              id="minTP"
              name="minTP"
              value={formData.minTP}
              onChange={handleChange}
              className={`form-input ${errors.minTP ? 'input-error' : ''}`}
              min="0"
              step="100"
            />
            {errors.minTP && <span className="error-message">{errors.minTP}</span>}
            <span className="input-hint">
              {user && (user as any).savwarTP !== undefined 
                ? `Senin TP'n: ${(user as any).savwarTP}` 
                : "Katılmak için gereken minimum TP puanı"}
            </span>
          </div>

          {/* Açıklama (Opsiyonel) */}
          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Kabile Açıklaması (Opsiyonel)
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-textarea ${errors.description ? 'input-error' : ''}`}
              placeholder="Kabileniz hakkında kısa bir açıklama yazın..."
              rows={4}
              maxLength={200}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
            <span className="input-hint">{formData.description.length}/200 karakter</span>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="submit-error">
              ⚠️ {errors.submit}
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate("/savkabile")}
              className="cancel-button"
              disabled={isSubmitting}
            >
              İptal
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Oluşturuluyor..." : "🏛️ Kabile Oluştur"}
            </button>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <h4>ℹ️ Bilgi</h4>
            <ul>
              <li>Kabile oluşturduktan sonra otomatik olarak lider olursunuz</li>
              <li>Kabile bayrağı sıralamada dalgalanarak görünecek 🚩</li>
              <li>Kabile ayarlarını istediğiniz zaman değiştirebilirsiniz</li>
              <li>Üyeler kabul/red edebilir veya kabileden çıkarabilirsiniz</li>
              <li>TP puanı şartını karşılamayan kullanıcılar başvuramaz</li>
            </ul>
          </div>
        </form>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="crop-modal-overlay" onClick={handleCropCancel}>
          <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="crop-modal-header">
              <h3>🚩 Bayrak Bölgesini Seçin</h3>
              <button onClick={handleCropCancel} className="crop-modal-close">✕</button>
            </div>
            
            <div className="crop-modal-body">
              <div 
                className="crop-container"
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img 
                  id="crop-image"
                  src={tempImage} 
                  alt="Kırpılacak resim"
                  className="crop-image"
                  draggable={false}
                />
                <div 
                  className="crop-area"
                  style={{
                    left: `${(cropArea.x / (document.getElementById('crop-image') as HTMLImageElement)?.naturalWidth || 1) * 100}%`,
                    top: `${(cropArea.y / (document.getElementById('crop-image') as HTMLImageElement)?.naturalHeight || 1) * 100}%`,
                    width: `${(cropArea.width / (document.getElementById('crop-image') as HTMLImageElement)?.naturalWidth || 1) * 100}%`,
                    height: `${(cropArea.height / (document.getElementById('crop-image') as HTMLImageElement)?.naturalHeight || 1) * 100}%`,
                  }}
                  onMouseDown={handleMouseDown}
                >
                  <div className="crop-handles">
                    <div className="crop-handle crop-handle-nw"></div>
                    <div className="crop-handle crop-handle-ne"></div>
                    <div className="crop-handle crop-handle-sw"></div>
                    <div className="crop-handle crop-handle-se"></div>
                  </div>
                  <div className="crop-info">
                    🚩 Dalgalanacak Bölge
                  </div>
                </div>
              </div>

              <div className="crop-controls">
                <div className="crop-control-group">
                  <label>Genişlik:</label>
                  <button onClick={() => handleResize('width', -20)} className="resize-btn">-</button>
                  <span className="size-display">{Math.round(cropArea.width)}px</span>
                  <button onClick={() => handleResize('width', 20)} className="resize-btn">+</button>
                </div>
                <div className="crop-control-group">
                  <label>Yükseklik:</label>
                  <button onClick={() => handleResize('height', -20)} className="resize-btn">-</button>
                  <span className="size-display">{Math.round(cropArea.height)}px</span>
                  <button onClick={() => handleResize('height', 20)} className="resize-btn">+</button>
                </div>
              </div>

              <div className="crop-hint">
                💡 Bayrağın dalgalanacak bölgesini seçin ve boyutlandırın
              </div>
            </div>

            <div className="crop-modal-footer">
              <button onClick={handleCropCancel} className="crop-cancel-btn">
                İptal
              </button>
              <button onClick={handleCropComplete} className="crop-confirm-btn">
                ✓ Onayla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
